import type { ResourceCell } from '@/screens/workspace/components/resource/resource'
import { readLocale, translate } from '~/core/i18n'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const ORDINAL_RULES: [number, string][] = [
  [1, 'st'],
  [2, 'nd'],
  [3, 'rd'],
]

function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th'
  return ORDINAL_RULES.find(([d]) => day % 10 === d)?.[1] ?? 'th'
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/**
 * Read per call rather than captured: this module has no component to subscribe to
 * the locale context, and a value frozen at import time would keep the language the
 * first list happened to render in.
 */
function say(key: string, vars?: Record<string, string | number>): string {
  return translate(readLocale(), key, vars)
}

function formatFullDate(date: Date): string {
  const day = date.getDate()
  return say('{month} {day}, {year}', {
    month: say(MONTH_NAMES[date.getMonth()]),
    // The ordinal is English orthography — 中文 numbers the day plainly, and
    // "3月4th日" is what appending it regardless produces.
    day: readLocale() === 'en' ? `${day}${ordinalSuffix(day)}` : day,
    year: date.getFullYear(),
  })
}

function pluralize(value: number, unit: string): string {
  return say(value === 1 ? `{count} ${unit}` : `{count} ${unit}s`, { count: value })
}

/**
 * Formats a date string into a human-friendly relative time label.
 *
 * - Within ~1 minute of now: "Now"
 * - Under 1 hour: "X minute(s) ago" / "X minute(s)"
 * - Under 24 hours: "X hour(s) ago" / "X hour(s)"
 * - Under 2 days: "X day(s) ago" / "X day(s)"
 * - Beyond 2 days: full date (e.g. "March 4th, 2026")
 */
export function timeCell(dateValue: string | Date | null | undefined): ResourceCell {
  if (!dateValue) return { label: null }

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const absDiff = Math.abs(diff)
  const isPast = diff > 0

  if (absDiff < MINUTE) return { label: say('Now') }

  if (absDiff < HOUR) {
    const minutes = Math.floor(absDiff / MINUTE)
    return {
      label: isPast
        ? say('{elapsed} ago', { elapsed: pluralize(minutes, 'minute') })
        : pluralize(minutes, 'minute'),
    }
  }

  if (absDiff < DAY) {
    const hours = Math.floor(absDiff / HOUR)
    return {
      label: isPast
        ? say('{elapsed} ago', { elapsed: pluralize(hours, 'hour') })
        : pluralize(hours, 'hour'),
    }
  }

  if (absDiff < 2 * DAY) {
    const days = Math.floor(absDiff / DAY)
    return {
      label: isPast
        ? say('{elapsed} ago', { elapsed: pluralize(days, 'day') })
        : pluralize(days, 'day'),
    }
  }

  return { label: formatFullDate(date) }
}
